import { useState, useCallback } from 'react'
import { filesApi } from '../../services/api'
import { dialog } from '../../services/dialog'

export interface UploadEntry {
  id: string
  name: string
  pct: number
  status: 'uploading' | 'done' | 'error'
}

export function useFilesUpload() {
  const [uploads, setUploads] = useState<UploadEntry[]>([])

  const upload = useCallback(async (file: File, folderId: string) => {
    const id = crypto.randomUUID()
    const entry: UploadEntry = { id, name: file.name, pct: 0, status: 'uploading' }
    setUploads((prev) => [...prev, entry])

    const updateEntry = (patch: Partial<UploadEntry>) => {
      setUploads((prev) =>
        prev.map((u) => (u.id === id ? { ...u, ...patch } : u))
      )
    }

    try {
      const { data: presign } = await filesApi.presignUpload(
        folderId,
        file.name,
        file.size,
        file.type || 'application/octet-stream'
      )

      // Use XHR for progress tracking
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.open('PUT', presign.uploadUrl)
        xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream')

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            updateEntry({ pct: Math.round((e.loaded / e.total) * 100) })
          }
        }

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve()
          } else {
            reject(new Error(`Upload failed: ${xhr.status}`))
          }
        }

        xhr.onerror = () => reject(new Error('Network error during upload'))
        xhr.send(file)
      })

      updateEntry({ pct: 100, status: 'uploading' })
      await filesApi.finalize(presign.objectId)
      updateEntry({ pct: 100, status: 'done' })
    } catch (err) {
      updateEntry({ status: 'error' })
      await dialog.alert({
        title: 'Upload failed',
        message: err instanceof Error ? err.message : 'Failed to upload file. Please try again.',
        tone: 'destructive',
      })
    }
  }, [])

  const clearDone = useCallback(() => {
    setUploads((prev) => prev.filter((u) => u.status !== 'done'))
  }, [])

  return { uploads, upload, clearDone }
}
